import type { FetchQueryOptions } from '@tanstack/vue-query'
import { Redirect, type RedirectRouteKeys } from '~~/shared-components/redirect/settings'
import type { Locale } from 'vue-i18n'
import { EMPTY_ARRAY } from '../content/const'

export default defineNuxtPlugin({
  name: 'link',
  setup: () => {
    addRouteMiddleware('link', async (to) => {
      const nuxtApp = useNuxtApp()
      const { public: config } = useRuntimeConfig()
      const { $localePath } = useNuxtApp()
      const { locale } = useLocale()
      const router = useRouter()

      const locales =
        config.locales ??
        (nuxtApp.$sharedComponentsOptions &&
        'options' in nuxtApp.$sharedComponentsOptions &&
        nuxtApp.$sharedComponentsOptions?.options &&
        'i18n' in nuxtApp.$sharedComponentsOptions?.options &&
        typeof nuxtApp.$sharedComponentsOptions?.options?.i18n === 'object' &&
        'locales' in nuxtApp.$sharedComponentsOptions?.options?.i18n
          ? nuxtApp.$sharedComponentsOptions?.options?.i18n?.locales
          : undefined) ??
        {}

      const [short, long] =
        Object.entries(locales).find(([, v]) => v === locale.value) ?? EMPTY_ARRAY

      let newLocale: { short: Locale; long: string } = { short: short as Locale, long }

      const hasPreview = !!to.query.expires && !!to.query.signature

      if (to.query.locale) {
        const l = Object.entries(locales).find(([, v]) => v === to.query.locale)
        if (l) {
          newLocale = { short: l[0]! as Locale, long: l[1] }
        }
      }

      const RedirectInstance = Redirect.getInstance()

      const route =
        RedirectInstance.getDefaultRedirectRoutes()[to.query.action as RedirectRouteKeys] ??
        RedirectInstance.getExtendedRedirectRoute(to.query.action as string)

      if (route) {
        if (route.customAction) {
          return route.customAction(to.query, newLocale.short)
        } else if (route.queryOption) {
          const queryClient = useQueryClient()
          try {
            if (!to.query.id) {
              return createError({ statusCode: 422, message: 'Missing ID' })
            }
            const data = (await queryClient.fetchQuery(
              route.queryOption({
                id: to.query.id as string | undefined,
                hasPreview,
                expires: to.query.expires as string | undefined,
                signature: to.query.signature as string | undefined,
                origin: to.query.origin as string | undefined,
                dataLang: newLocale.long,
                query: to.query,
              }) as FetchQueryOptions
            )) as { _link: Link }
            const linkData = route.linkParser(data, to.query)
            const clearQuery = { ...to.query }
            delete clearQuery.action
            delete clearQuery.id
            delete clearQuery.locale
            const link = await nuxtApp.runWithContext(() =>
              redirect(linkData?.type as LinkTypes, linkData, undefined, clearQuery, data)
            )

            if (typeof link === 'string') {
              return nuxtApp.runWithContext(() => {
                const resolvedRoute = router.resolve(link)
                const routeBaseName = useRouteBaseName()
                const newRoute = {
                  ...resolvedRoute,
                  name: routeBaseName(resolvedRoute.name as string),
                  query: {
                    ...resolvedRoute?.query,
                    ...(to.query._bmid ? { _bmid: to.query._bmid } : {}),
                    ...(to.query.expires ? { expires: to.query.expires } : {}),
                    ...(to.query.signature ? { signature: to.query.signature } : {}),
                    ...(to.query.origin ? { origin: to.query.origin } : {}),
                  },
                }
                return navigateTo($localePath(newRoute, newLocale.short))
              })
            }
          } catch (e) {
            console.error(e)
            return createError(e as string)
          }
        } else {
          return nuxtApp.runWithContext(() =>
            navigateTo(
              $localePath(
                {
                  name:
                    typeof route.pageName === 'function'
                      ? route.pageName(to.query)
                      : route.pageName,
                  query: {
                    ...(route.query ? route.query(to.query) : {}),
                    ...(to.query._bmid ? { _bmid: to.query._bmid } : {}),
                    ...(to.query.expires ? { expires: to.query.expires } : {}),
                    ...(to.query.signature ? { signature: to.query.signature } : {}),
                    ...(to.query.origin ? { origin: to.query.origin } : {}),
                  },
                  params: route.params ? route.params(to.query) : undefined,
                  hash: route.hash ? route.hash(to.query) : undefined,
                },
                newLocale.short
              )
            )
          )
        }
      }

      return createError({ statusCode: 422, statusMessage: 'Bad action type' })
    })
  },
})
